import { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface PasswordStrengthMeterProps {
  password: string;
  /** Minimum length requirement (default: 8) */
  minLength?: number;
  /** Whether to require a special character (default: true) */
  requireSpecial?: boolean;
  className?: string;
}

type StrengthLevel = 'empty' | 'too_short' | 'weak' | 'medium' | 'strong';

const strengthConfig: Record<StrengthLevel, { label: string; color: string; bars: number }> = {
  empty: { label: '', color: '', bars: 0 },
  too_short: { label: 'Too short', color: 'bg-red-500', bars: 1 },
  weak: { label: 'Weak', color: 'bg-red-500', bars: 1 },
  medium: { label: 'Medium', color: 'bg-yellow-500', bars: 2 },
  strong: { label: 'Strong', color: 'bg-dyndev-green', bars: 3 },
};

function evaluateStrength(
  password: string,
  minLength: number,
  requireSpecial: boolean,
): StrengthLevel {
  if (!password) return 'empty';
  if (password.length < 6) return 'too_short';

  let score = 0;
  if (password.length >= minLength) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (requireSpecial && /[!@#$%^&*(),.?":{}|<>_\-+=\[\]~`]/.test(password)) score++;
  if (!requireSpecial && /[!@#$%^&*(),.?":{}|<>_\-+=\[\]~`]/.test(password)) score++;

  if (score <= 1) return 'weak';
  if (score === 2) return 'medium';
  return 'strong';
}

export function PasswordStrengthMeter({
  password,
  minLength = 8,
  requireSpecial = true,
  className,
}: PasswordStrengthMeterProps) {
  const strength = useMemo(
    () => evaluateStrength(password, minLength, requireSpecial),
    [password, minLength, requireSpecial],
  );

  if (strength === 'empty') return null;

  const config = strengthConfig[strength];

  return (
    <div className={cn('space-y-1', className)}>
      {/* Bar */}
      <div className="flex gap-1 h-1">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              'flex-1 rounded-full transition-all duration-300',
              i <= config.bars ? config.color : 'bg-[var(--border-color)]',
            )}
          />
        ))}
      </div>
      {/* Label */}
      <p
        className={cn(
          'text-[10px] font-medium transition-colors duration-300',
          strength === 'too_short' || strength === 'weak' ? 'text-red-400' :
          strength === 'medium' ? 'text-yellow-400' :
          strength === 'strong' ? 'text-dyndev-green' :
          'text-[var(--text-secondary)]',
        )}
      >
        {config.label}
        {strength === 'too_short' && ` — ${password.length}/6 min`}
        {strength === 'weak' && ' — add mixed case, numbers, or symbols'}
      </p>
    </div>
  );
}
